import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs/operators';
import { EventMessage, EventType, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { protectedResources } from '../auth-config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly loggedIn = new BehaviorSubject<boolean>(false);
  private currentUser: AccountInfo | null = null;

  constructor(private readonly msalService: MsalService, private readonly msalBroadcastService: MsalBroadcastService) {
    this.setLoginDisplay();

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.setLoginDisplay();
      });
  }

  login(): void {
    this.msalService.loginRedirect();
  }

  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  }

  get isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  get activeAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  private setLoginDisplay(): void {
    const account = this.msalService.instance.getAllAccounts()[0] || null;
    this.msalService.instance.setActiveAccount(account);
    this.loggedIn.next(!!account);
    this.currentUser = account;
  }

  getUserEmail(): string | null {
    return this.currentUser?.username ?? null;
  }

  handleRedirect(): void {
    this.msalService.instance.handleRedirectPromise()
      .then((result: AuthenticationResult | null) => {
        if (result !== null && result.account !== null) {
          this.msalService.instance.setActiveAccount(result.account);
        } else {
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            this.msalService.instance.setActiveAccount(accounts[0]);
          }
        }
      }).catch(error => {
        console.error('Redirect handling error:', error);
      });
  }
  async acquireAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return Promise.reject(new Error('No active account! Ensure user is logged in.'));
    }

    try {
      const response = await this.msalService.instance.acquireTokenSilent({
        scopes: protectedResources.functionApi.scopes,
        account: account
      });
      return response.accessToken;
    } catch (error) {
      console.error('Token acquisition failed', error);
      throw error;
    }
  }
}
